import type {
    Firestore,
    DocumentData,
    DocumentChange,
} from 'firebase/firestore'
import { collection, onSnapshot } from 'firebase/firestore'
import type { Services } from '~/services/types'
import type {
    Invoice,
    BankTransaction,
    DocumentTransactionAssociation,
} from '~/storage/types'

export function setupRealtimeInvoiceAndTransactionListeners(
    db: Firestore,
    services: Services,
) {
    let unsubscribeInvoices: (() => void) | null = null
    let unsubscribeTransactions: (() => void) | null = null
    let unsubscribeAssociations: (() => void) | null = null
    let matchTimer: NodeJS.Timeout | null = null
    let invoiceQueue: string[] = []
    let transactionQueue: string[] = []

    const isAttached = () =>
        Boolean(unsubscribeInvoices != null || unsubscribeTransactions != null)

    const clearQueuesAndTimer = () => {
        invoiceQueue = []
        transactionQueue = []
        if (matchTimer) {
            clearTimeout(matchTimer)
            matchTimer = null
        }
    }

    const detach = () => {
        if (unsubscribeInvoices) unsubscribeInvoices()
        if (unsubscribeTransactions) unsubscribeTransactions()
        if (unsubscribeAssociations) unsubscribeAssociations()
        unsubscribeInvoices = null
        unsubscribeTransactions = null
        unsubscribeAssociations = null
        clearQueuesAndTimer()
    }

    async function processWithConcurrencyLimit<T>(
        items: T[],
        concurrency: number,
        worker: (item: T) => Promise<void>,
    ): Promise<void> {
        let index = 0
        const inFlight = new Set<Promise<void>>()

        const launchNext = () => {
            if (index >= items.length) return
            const item = items[index++]
            const p = worker(item)
                .catch(() => {})
                .finally(() => {
                    inFlight.delete(p)
                })
            inFlight.add(p)
        }

        while (index < items.length || inFlight.size > 0) {
            while (inFlight.size < concurrency && index < items.length) {
                launchNext()
            }
            if (inFlight.size > 0) {
                await Promise.race(inFlight)
            }
        }
    }

    const scheduleMatchProcessing = () => {
        if (matchTimer) return
        matchTimer = setTimeout(async () => {
            const { transactionInvoiceMatching } = services

            const invIds = [...invoiceQueue]
            const txIds = [...transactionQueue]
            invoiceQueue = []
            transactionQueue = []

            const CONCURRENCY = 10

            await processWithConcurrencyLimit(
                invIds,
                CONCURRENCY,
                async (id) => {
                    await transactionInvoiceMatching.findMatchesForInvoice(
                        id,
                        false,
                    )
                },
            )

            await processWithConcurrencyLimit(
                txIds,
                CONCURRENCY,
                async (id) => {
                    await transactionInvoiceMatching.findMatchesForTransaction(
                        id,
                        false,
                    )
                },
            )

            matchTimer = null
        }, 500)
    }

    const attach = () => {
        const userId = services.cache.getKey('authState').user?.uid
        if (!userId) return

        let isInitialInvoiceSnapshot = true
        let isInitialTransactionSnapshot = true
        let isInitialAssociationsSnapshot = true

        const documentsRef = collection(db, 'users', userId, 'documents')
        unsubscribeInvoices = onSnapshot(documentsRef, async (snapshot) => {
            // Skip processing the initial snapshot - it contains all existing documents
            if (isInitialInvoiceSnapshot) {
                isInitialInvoiceSnapshot = false
                return
            }

            const addedChanges: DocumentChange<DocumentData>[] = snapshot
                .docChanges()
                .filter(
                    (change) =>
                        change.type === 'added' &&
                        !change.doc.metadata.hasPendingWrites,
                )
            if (addedChanges.length === 0) return

            const addedInvoices = addedChanges.map((change) => {
                const data = change.doc.data()
                return {
                    id: change.doc.id,
                    ...data,
                } as Invoice
            })
            services.cache.setInvoice(addedInvoices)

            const nonGmiIds = addedInvoices
                .filter((i) => !i.id.startsWith('GMI:'))
                .map((i) => i.id)

            const gmiInvoices = addedInvoices.filter((i) =>
                i.id.startsWith('GMI:'),
            )

            const gmiIdsToMatch: string[] = []
            for (const invoice of gmiInvoices) {
                const hasExisting =
                    await services.transactionInvoiceMatching.hasAssociationsForInvoice(
                        invoice.id,
                    )
                if (!hasExisting) {
                    gmiIdsToMatch.push(invoice.id)
                }
            }

            const idsToMatch = [...nonGmiIds, ...gmiIdsToMatch]
            if (idsToMatch.length > 0) {
                invoiceQueue.push(...idsToMatch)
                scheduleMatchProcessing()
            }
        })

        const transactionsRef = collection(db, 'users', userId, 'transactions')
        unsubscribeTransactions = onSnapshot(transactionsRef, (snapshot) => {
            // Skip processing the initial snapshot - it contains all existing documents
            if (isInitialTransactionSnapshot) {
                isInitialTransactionSnapshot = false
                return
            }

            const addedChanges: DocumentChange<DocumentData>[] = snapshot
                .docChanges()
                .filter(
                    (change) =>
                        change.type === 'added' &&
                        !change.doc.metadata.hasPendingWrites,
                )
            if (addedChanges.length === 0) return

            const addedTransactions = addedChanges.map((change) => {
                const data = change.doc.data()
                return {
                    id: change.doc.id,
                    ...data,
                } as BankTransaction
            })
            services.cache.setTransaction(addedTransactions)

            const idsToMatch = addedTransactions
                .filter((t) => !t.id.startsWith('GMI:'))
                .map((t) => t.id)
            if (idsToMatch.length > 0) {
                transactionQueue.push(...idsToMatch)
                scheduleMatchProcessing()
            }
        })

        const associationsRef = collection(
            db,
            'users',
            userId,
            'documentTransactionAssociations',
        )
        unsubscribeAssociations = onSnapshot(associationsRef, (snapshot) => {
            if (isInitialAssociationsSnapshot) {
                isInitialAssociationsSnapshot = false
                return
            }

            const changes = snapshot.docChanges()
            const addedModifiedChanges = changes.filter(
                (change) =>
                    (change.type === 'added' || change.type === 'modified') &&
                    !change.doc.metadata.hasPendingWrites,
            )
            const addedModifiedAssocs: DocumentTransactionAssociation[] =
                addedModifiedChanges.map((change) => {
                    const data = change.doc.data()
                    return {
                        id: change.doc.id,
                        ...data,
                    } as DocumentTransactionAssociation
                })

            const removedChanges = changes.filter(
                (change) => change.type === 'removed',
            )
            const removedIds = removedChanges.map((change) => change.doc.id)

            if (addedModifiedAssocs.length === 0 && removedIds.length === 0)
                return

            let currentAssocs = {
                ...services.cache.getKey('documentTransactionAssociations'),
            }

            const removedAssocs = removedIds
                .map((id) => currentAssocs[id])
                .filter(
                    (a): a is DocumentTransactionAssociation => a !== undefined,
                )

            addedModifiedAssocs.forEach((assoc) => {
                currentAssocs[assoc.id] = assoc
            })

            removedIds.forEach((id) => {
                delete currentAssocs[id]
            })

            const matchesByTransaction: Record<string, string[]> = {}
            const matchesByInvoice: Record<string, string[]> = {}
            Object.values(currentAssocs).forEach((assoc) => {
                if (!matchesByTransaction[assoc.transactionId]) {
                    matchesByTransaction[assoc.transactionId] = []
                }
                matchesByTransaction[assoc.transactionId].push(assoc.id)

                if (!matchesByInvoice[assoc.documentId]) {
                    matchesByInvoice[assoc.documentId] = []
                }
                matchesByInvoice[assoc.documentId].push(assoc.id)
            })

            services.cache.setKeys({
                documentTransactionAssociations: currentAssocs,
                matchesByTransaction,
                matchesByInvoice,
            })

            services.cache.onUpdate.emit(
                new Set([
                    'documentTransactionAssociations',
                    'matchesByTransaction',
                    'matchesByInvoice',
                ]),
            )

            const changedAssocIds = new Set([
                ...addedModifiedAssocs.map((a) => a.id),
                ...removedIds,
            ])
            services.cache.onMatchingAssociationsUpdate.emit(changedAssocIds)

            const affectedInvoiceIds = new Set<string>()
            const affectedTransactionIds = new Set<string>()

            addedModifiedAssocs.forEach((assoc) => {
                affectedInvoiceIds.add(assoc.documentId)
                affectedTransactionIds.add(assoc.transactionId)
            })

            removedAssocs.forEach((assoc) => {
                affectedInvoiceIds.add(assoc.documentId)
                affectedTransactionIds.add(assoc.transactionId)
            })

            if (affectedInvoiceIds.size > 0) {
                services.cache.onInvoicesWithAssociationChanges.emit(
                    affectedInvoiceIds,
                )
            }
            if (affectedTransactionIds.size > 0) {
                services.cache.onTransactionsWithAssociationChanges.emit(
                    affectedTransactionIds,
                )
            }
        })
    }

    const shouldAttach = () => {
        const userId = services.cache.getKey('authState').user?.uid
        const loadState = services.cache.getKey('loadState')
        return Boolean(userId && loadState === 'success')
    }

    const evaluate = () => {
        if (shouldAttach()) {
            if (!isAttached()) {
                attach()
            }
        } else {
            if (isAttached()) {
                detach()
            }
        }
    }

    // react to auth changes: attach/detach appropriately
    const unsubscribeAuth = services.auth.onAuthStateChanged(() => {
        evaluate()
    })

    // react to cache state changes (authState/loadState)
    const unsubscribeCache = services.cache.onUpdate.listen((keys) => {
        if (keys.has('authState') || keys.has('loadState')) {
            evaluate()
        }
    })

    // Initial evaluation
    evaluate()

    return () => {
        detach()
        unsubscribeAuth()
        if (unsubscribeCache) unsubscribeCache()
        if (unsubscribeAssociations) unsubscribeAssociations()
        if (matchTimer) clearTimeout(matchTimer)
    }
}
